import { useRef, useState } from "react";
import { Camera, X, RotateCcw, Check, ImageIcon } from "lucide-react";

/**
 * ProofCameraModal — lets a driver capture a photo (or upload one) as proof of delivery.
 * Uses a hidden <input type="file" accept="image/*"> so mobile users can still choose the
 * camera and desktop users get a standard file picker. Image is compressed to a reasonable
 * size before being handed back as a base64 data URL.
 */
export default function ProofCameraModal({ onClose, onCapture, title, subtitle, cta }) {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    const base64 = await compressImage(file, 1280, 0.78);
    setPreview(base64);
  };

  const submit = async () => {
    if (!preview) return;
    setUploading(true);
    try {
      await onCapture(preview);
    } finally {
      setUploading(false);
    }
  };

  const headerTitle = title || "Proof of delivery";
  const headerSubtitle = subtitle || "Snap the drop-off";
  const submitLabel = cta || "Submit & complete delivery";

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6 fade-up"
      data-testid="proof-camera-modal"
      role="dialog"
      aria-modal="true"
    >
      {/* Blurred, darkened backdrop */}
      <div className="absolute inset-0 bg-black/75 backdrop-blur-2xl backdrop-saturate-150" />

      {/* Floating glass card */}
      <div
        className="relative w-full max-w-md sm:max-w-lg rounded-[32px] border-2 border-eatup-green/50 p-5 sm:p-6 shadow-[0_40px_120px_rgba(0,0,0,0.6)] ring-1 ring-white/10 flex flex-col max-h-[92vh] overflow-hidden"
        style={{
          background:
            "radial-gradient(120% 120% at 0% 0%, rgba(24,180,91,0.18) 0%, rgba(20,20,22,0.85) 60%)",
          backdropFilter: "blur(40px) saturate(180%)",
          WebkitBackdropFilter: "blur(40px) saturate(180%)",
        }}
      >
        <div className="flex items-center justify-between text-white">
          <button onClick={onClose} data-testid="proof-close-btn" className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center transition">
            <X className="w-5 h-5" />
          </button>
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.4em] text-eatup-yellow font-black">{headerTitle}</div>
            <div className="font-display font-black text-lg drop-shadow-md">{headerSubtitle}</div>
          </div>
          <div className="w-10 h-10" />
        </div>

        <div className="flex-1 overflow-y-auto flex items-center justify-center px-2 py-5">
          {preview ? (
            <div className="w-full">
              <img src={preview} alt="Proof preview" data-testid="proof-preview" className="w-full rounded-3xl object-cover shadow-[0_20px_60px_rgba(0,0,0,0.5)] max-h-[52vh] border-2 border-white/15" />
              <p className="mt-4 text-center text-white/80 text-sm">
                Make sure the food is clearly visible. Tap <span className="text-eatup-yellow font-bold">Submit</span> when it looks good.
              </p>
            </div>
          ) : (
            <div className="text-center text-white">
              <div className="relative mx-auto w-24 h-24 rounded-full bg-eatup-green/20 flex items-center justify-center">
                <span className="absolute inset-0 rounded-full bg-eatup-green/25 animate-ping" />
                <Camera className="relative w-12 h-12 text-eatup-green drop-shadow-[0_0_12px_rgba(24,180,91,0.7)]" />
              </div>
              <h2 className="mt-5 font-display text-2xl font-black">Waiting for camera…</h2>
              <p className="mt-2 text-white/70 text-sm">
                If nothing opens, tap the button below to try again.
              </p>
              <button
                onClick={() => fileInputRef.current?.click()}
                data-testid="proof-open-camera-btn"
                className="mt-6 inline-flex items-center gap-2 bg-eatup-green hover:bg-eatup-green-dark text-white rounded-full px-6 py-3 font-black transition shadow-[0_10px_30px_rgba(24,180,91,0.4)]"
              >
                <Camera className="w-4 h-4" /> Open camera
              </button>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="proof-file-input"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
        </div>

        <div className="space-y-2.5 shrink-0">
          {preview ? (
            <>
              <button
                onClick={submit}
                disabled={uploading}
                data-testid="proof-submit-btn"
                className="w-full bg-eatup-green hover:bg-eatup-green-dark text-white rounded-full py-4 font-display font-black text-lg transition flex items-center justify-center gap-2 disabled:opacity-60 shadow-[0_10px_30px_rgba(24,180,91,0.4)] active:scale-[0.98]"
              >
                <Check className="w-5 h-5" /> {uploading ? "Sending…" : submitLabel}
              </button>
              <button
                onClick={() => { setPreview(null); setTimeout(() => fileInputRef.current?.click(), 100); }}
                disabled={uploading}
                data-testid="proof-retake-btn"
                className="w-full py-2.5 text-white/80 hover:text-white font-semibold text-sm transition flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-4 h-4" /> Retake
              </button>
            </>
          ) : (
            <button
              onClick={() => fileInputRef.current?.click()}
              data-testid="proof-choose-btn"
              className="w-full bg-white/10 hover:bg-white/20 border border-white/10 text-white rounded-full py-3 font-semibold text-sm transition flex items-center justify-center gap-2"
            >
              <ImageIcon className="w-4 h-4" /> Choose from library
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

async function compressImage(file, maxDim = 1280, quality = 0.78) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}
